// Consumers admin route — /admin/consumers
//
// Each consumer is one API-key holder bound to a single provider. The
// upstream remote_model comes from each request's `model` field at chat
// time, so the issuance form only asks the operator to pick a provider
// — no model field, no datalist, no discover-models lookup.
//
// Server returns the raw key ONCE on create+rotate; the issuance modal
// shows it large + copy-to-clipboard + no-auto-close so the operator
// has to acknowledge they saved it.

import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Copy, Check } from "lucide-react"
import {
  Badge,
  Button,
  ConfirmDialog,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
} from "@/admin-design/_common"
import {
  llmApi,
  type Consumer,
  type ConsumerCreated,
  type ConsumerCreateInput,
  type ConsumerUpdateInput,
} from "@/api/llm"

function formatDate(iso?: string): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

interface ConsumerForm {
  name: string
  note: string
  provider_id: string
}

const EMPTY_FORM: ConsumerForm = {
  name: "",
  note: "",
  provider_id: "",
}

function toCreateInput(f: ConsumerForm): { ok: true; payload: ConsumerCreateInput } | { ok: false; error: string } {
  if (!f.name.trim()) return { ok: false, error: "name is required" }
  if (!f.provider_id) return { ok: false, error: "provider is required" }
  const pid = Number.parseInt(f.provider_id, 10)
  if (!Number.isFinite(pid) || pid <= 0) return { ok: false, error: "provider is required" }
  return {
    ok: true,
    payload: {
      name: f.name.trim(),
      note: f.note.trim() || undefined,
      provider_id: pid,
    },
  }
}

export default function Consumers() {
  const qc = useQueryClient()
  const consumers = useQuery({ queryKey: ["admin", "consumers"], queryFn: llmApi.listConsumers })
  const providers = useQuery({ queryKey: ["admin", "providers"], queryFn: llmApi.listProviders })

  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<ConsumerForm>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)

  const [issued, setIssued] = useState<ConsumerCreated | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Consumer | null>(null)
  const [confirmRotate, setConfirmRotate] = useState<Consumer | null>(null)
  const [editing, setEditing] = useState<Consumer | null>(null)
  const [editForm, setEditForm] = useState<ConsumerForm>(EMPTY_FORM)
  const [editError, setEditError] = useState<string | null>(null)

  const providerOptions = useMemo(() => {
    if (!providers.data) return []
    return providers.data.map((p) => ({
      label: p.enabled ? p.name : `${p.name} (disabled)`,
      value: String(p.id),
    }))
  }, [providers.data])

  const createMut = useMutation({
    mutationFn: (b: ConsumerCreateInput) => llmApi.createConsumer(b),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "consumers"] })
      setIssued(res)
      setCreating(false)
      setForm(EMPTY_FORM)
    },
    onError: (e: Error) => setFormError(e.message),
  })
  const rotateMut = useMutation({
    mutationFn: (id: number) => llmApi.rotateConsumer(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "consumers"] })
      setIssued(res)
      setConfirmRotate(null)
    },
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => llmApi.deleteConsumer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consumers"] })
      setConfirmDelete(null)
    },
  })
  const editMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ConsumerUpdateInput }) =>
      llmApi.updateConsumer(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consumers"] })
      setEditing(null)
    },
    onError: (e: Error) => setEditError(e.message),
  })

  // Reset the form fields whenever the modal opens so stale entries from
  // a previous attempt don't leak.
  useEffect(() => {
    if (creating) {
      setForm(EMPTY_FORM)
      setFormError(null)
    }
  }, [creating])

  // Pre-fill the edit form from the row whenever the edit modal opens.
  useEffect(() => {
    if (editing) {
      setEditForm({
        name: editing.name,
        note: editing.note ?? "",
        provider_id: String(editing.provider_id),
      })
      setEditError(null)
    }
  }, [editing])

  const onSubmit = () => {
    const built = toCreateInput(form)
    if (!built.ok) {
      setFormError(built.error)
      return
    }
    createMut.mutate(built.payload)
  }

  const onSubmitEdit = () => {
    if (!editing) return
    if (!editForm.name.trim()) {
      setEditError("name is required")
      return
    }
    if (!editForm.provider_id) {
      setEditError("provider is required")
      return
    }
    const pid = Number.parseInt(editForm.provider_id, 10)
    if (!Number.isFinite(pid) || pid <= 0) {
      setEditError("provider is required")
      return
    }
    // Build a partial body containing only the fields that actually changed.
    // Empty `note` is sent as "" so the server can clear it.
    const body: ConsumerUpdateInput = {}
    if (editForm.name.trim() !== editing.name) body.name = editForm.name.trim()
    if ((editForm.note ?? "") !== (editing.note ?? "")) body.note = editForm.note.trim()
    if (pid !== editing.provider_id) body.provider_id = pid
    if (Object.keys(body).length === 0) {
      setEditing(null)
      return
    }
    editMut.mutate({ id: editing.id, body })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">Consumers</h1>
        <Button size="sm" onClick={() => setCreating(true)}>
          + Add consumer
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <Table.Head>
            <Table.Row interactive={false}>
              <Table.Header>Name</Table.Header>
              <Table.Header>Key prefix</Table.Header>
              <Table.Header>Provider</Table.Header>
              <Table.Header>Status</Table.Header>
              <Table.Header>Last used</Table.Header>
              <Table.Header>Created</Table.Header>
              <Table.Header className="text-right">Actions</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {consumers.isLoading && (
              <Table.Row interactive={false}>
                <Table.Cell colSpan={7} className="text-center text-muted-foreground">
                  Loading…
                </Table.Cell>
              </Table.Row>
            )}
            {consumers.data && consumers.data.length === 0 && (
              <Table.Empty
                colSpan={7}
                title="No consumers yet"
                description="Issue a key so a downstream service can call /v1/chat/completions."
              />
            )}
            {consumers.data?.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell>
                  <div className="font-medium">{c.name}</div>
                  {c.note && <div className="mt-0.5 text-xs text-muted-foreground">{c.note}</div>}
                </Table.Cell>
                <Table.Cell>
                  <code className="text-xs text-muted-foreground">{c.key_prefix}…</code>
                </Table.Cell>
                <Table.Cell>{c.provider_name ?? <span className="text-muted-foreground">—</span>}</Table.Cell>
                <Table.Cell>
                  {c.status === "active" ? (
                    <Badge variant="success">active</Badge>
                  ) : (
                    <Badge variant="destructive">revoked</Badge>
                  )}
                </Table.Cell>
                <Table.Cell className="text-muted-foreground">{formatDate(c.last_used_at)}</Table.Cell>
                <Table.Cell className="text-muted-foreground">{formatDate(c.created_at)}</Table.Cell>
                <Table.Cell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setEditing(c)}
                      disabled={c.status !== "active"}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setConfirmRotate(c)}
                      disabled={c.status !== "active"}
                    >
                      Rotate
                    </Button>
                    <Button
                      variant="destructive"
                      size="xs"
                      onClick={() => setConfirmDelete(c)}
                    >
                      {c.status === "active" ? "Revoke" : "Delete"}
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      {creating && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setCreating(false)} aria-hidden="true" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-border px-6 py-4">
                <h2 className="text-base font-semibold text-card-foreground">Add consumer</h2>
              </div>
              <div className="space-y-4 px-6 py-4">
                <FormRow label="Name" required stacked>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="acme-prod"
                    autoFocus
                  />
                </FormRow>
                <FormRow
                  label="Provider"
                  required
                  stacked
                  help="The downstream chat request's `model` field is forwarded to this provider as remote_model."
                >
                  <Select
                    value={form.provider_id}
                    onValueChange={(v) => setForm({ ...form, provider_id: v })}
                    options={providerOptions}
                    placeholder={
                      providerOptions.length === 0 ? "No providers — add one first" : "Choose provider"
                    }
                  />
                </FormRow>
                <FormRow label="Note" stacked help="Optional free-text description.">
                  <Textarea
                    rows={3}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Used by acme.com production fleet."
                  />
                </FormRow>
                {formError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-sm text-destructive-soft-foreground">
                    {formError}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
                <Button variant="secondary" size="sm" onClick={() => setCreating(false)} disabled={createMut.isPending}>
                  Cancel
                </Button>
                <Button size="sm" loading={createMut.isPending} onClick={onSubmit}>
                  Create
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {editing && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setEditing(null)} aria-hidden="true" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-border px-6 py-4">
                <h2 className="text-base font-semibold text-card-foreground">
                  Edit consumer "{editing.name}"
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  The existing API key keeps working — only the binding changes. Use Rotate to issue a new key.
                </p>
              </div>
              <div className="space-y-4 px-6 py-4">
                <FormRow label="Name" required stacked>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    autoFocus
                  />
                </FormRow>
                <FormRow label="Provider" required stacked>
                  <Select
                    value={editForm.provider_id}
                    onValueChange={(v) => setEditForm({ ...editForm, provider_id: v })}
                    options={providerOptions}
                    placeholder={
                      providerOptions.length === 0 ? "No providers — add one first" : "Choose provider"
                    }
                  />
                </FormRow>
                <FormRow label="Note" stacked help="Optional free-text description.">
                  <Textarea
                    rows={3}
                    value={editForm.note}
                    onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  />
                </FormRow>
                {editError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-sm text-destructive-soft-foreground">
                    {editError}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
                <Button variant="secondary" size="sm" onClick={() => setEditing(null)} disabled={editMut.isPending}>
                  Cancel
                </Button>
                <Button size="sm" loading={editMut.isPending} onClick={onSubmitEdit}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {issued && <IssuanceModal issued={issued} onClose={() => setIssued(null)} />}

      <ConfirmDialog
        open={confirmRotate !== null}
        onOpenChange={(o) => !o && setConfirmRotate(null)}
        title={`Rotate key for "${confirmRotate?.name ?? ""}"?`}
        description="The current key will stop working immediately. A new key is issued (with the same provider binding) and revealed once."
        confirmLabel="Rotate"
        confirmVariant="primary"
        loading={rotateMut.isPending}
        onConfirm={() => {
          if (confirmRotate) rotateMut.mutate(confirmRotate.id)
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={
          confirmDelete?.status === "revoked"
            ? `Permanently delete "${confirmDelete?.name ?? ""}"?`
            : `Revoke consumer "${confirmDelete?.name ?? ""}"?`
        }
        description={
          confirmDelete?.status === "revoked"
            ? "Hard-delete: the row is removed from the consumers table. Past usage_logs entries keep their denormalised consumer_name for audit, but the row is gone for good."
            : "Soft-delete: the row stays for audit, status flips to revoked, and the key stops working immediately. Click Delete again on the revoked row to remove it permanently."
        }
        confirmLabel={confirmDelete?.status === "revoked" ? "Delete" : "Revoke"}
        confirmVariant="destructive"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (confirmDelete) deleteMut.mutate(confirmDelete.id)
        }}
      />
    </div>
  )
}

function IssuanceModal({ issued, onClose }: { issued: ConsumerCreated; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(issued.raw_key)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select the text node so the user can copy manually.
      const el = document.getElementById("issued-key-block")
      if (el) {
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          className="w-full max-w-lg rounded-lg border border-border bg-card shadow-lg"
        >
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-card-foreground">
              API key for "{issued.name}"
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              이 키는 다시 표시되지 않습니다. 지금 저장하세요.
            </p>
          </div>
          <div className="space-y-3 px-6 py-4">
            <div
              id="issued-key-block"
              className="break-all rounded-md border border-border bg-muted px-3 py-3 font-mono text-sm text-foreground"
            >
              {issued.raw_key}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                prefix <code>{issued.key_prefix}</code>
              </span>
              <Button variant="secondary" size="sm" onClick={onCopy}>
                {copied ? (
                  <>
                    <Check className="h-4 w-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Copy
                  </>
                )}
              </Button>
            </div>
            <div className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
              Once you close this dialog the raw key cannot be retrieved. Rotate to get a new one.
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
            <Button size="sm" onClick={onClose}>
              I saved it
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
